<script setup lang="ts">
import { toRefs, computed } from "vue";
import BaseDisplayRule from "@/modules/rule_engine/components/BaseDisplayRule.vue";
import {
	RuleSetRule,
	isRuleSetRule,
} from "@/modules/rule_engine/ruleEngineClasses";
import DisplayConditions from "@/modules/rule_engine/components/conditions/DisplayConditions.vue";
import { useRuleEnginedStore } from "@/modules/rule_engine/store";
import { displayColorFor } from "@/modules/rule_engine/ruleTypeColors";
import { RULE_ENGINE_AI_SUMMARY_PERMISSION } from "@/modules/rule_engine/aiSummaryFormat";
import { useBaseStore } from "@/stores/base";

const emit = defineEmits(["edit-rule", "delete-rule", "clone-rule"]);

const props = defineProps<{
	rule: RuleSetRule;
	index: number;
	readOnly: boolean;
}>();

defineOptions({ name: "DisplayGroupRule" });

const { rule: groupRule, readOnly } = toRefs(props);

const componentIsSelected = computed(() =>
	useRuleEnginedStore().ruleEngineState.selectedRules.includes(
		groupRule.value.uuid
	)
);

const contents = computed(() => {
	const rules = groupRule.value.rule_set?.rules ?? [];

	return {
		groups: rules.filter(isRuleSetRule).length,
		rules: rules.filter((rule) => !isRuleSetRule(rule)).length,
	};
});

const groupAiSummary = computed(() =>
	useRuleEnginedStore().ruleSetAiSummary(groupRule.value.rule_set?.uuid)
);

// An existing summary describes the last-saved state, so unsaved edits to
// this group or its descendants hide it rather than show it as still
// accurate; a brand new, never-saved group falls under the same check.
const groupHasUnsavedChanges = computed(() =>
	useRuleEnginedStore().ruleSetHasUnsavedChanges(groupRule.value.rule_set?.uuid)
);

const hasAiSummaryPermission = computed(() =>
	useBaseStore().HAS_PERMISSION(RULE_ENGINE_AI_SUMMARY_PERMISSION)
);
</script>

<template>
	<base-display-rule
		:color-string="
			!componentIsSelected ? displayColorFor(groupRule.type) : 'lime-lighten-5'
		"
	>
		<template v-if="!readOnly" v-slot:prepend>
			<v-checkbox
				color="primary"
				hide-details
				v-model="useRuleEnginedStore().ruleEngineState.selectedRules"
				:value="groupRule.uuid"
			></v-checkbox>
		</template>
		<template v-slot:display>
			<div>
				<div class="d-flex align-center" style="min-height: 56px">
					<v-icon size="16" class="mr-2" style="opacity: 0.55">
						mdi-folder-outline
					</v-icon>
					<v-card-title class="pl-0">{{
						groupRule.rule_set.name
					}}</v-card-title>
					<span class="ml-3 contents-summary">
						{{ contents.rules }} rules
						<template v-if="contents.groups > 0">
							· {{ contents.groups }} groups
						</template>
					</span>
				</div>

				<div v-if="hasAiSummaryPermission" class="ai-summary-line">
					<template v-if="groupHasUnsavedChanges">
						AI summary will be available after saving.
					</template>
					<template v-else-if="groupAiSummary?.ai_summary_status === 'completed'">
						{{ groupAiSummary.ai_summary }}
					</template>
					<template v-else-if="groupAiSummary?.ai_summary_status === 'failed'">
						Summary unavailable.
					</template>
					<template v-else>
						<v-progress-circular
							indeterminate
							size="10"
							width="2"
							class="mr-1"
						/>
						Generating summary...
					</template>
				</div>

				<v-divider class="mb-2" v-if="rule?.conditions?.length > 0" />
				<display-conditions :conditions="rule?.conditions" />
			</div>
		</template>
		<template v-if="!readOnly" v-slot:actions>
			<v-btn
				@click="emit('edit-rule')"
				variant="text"
				icon
				size="small"
				density="comfortable"
				color="primary"
				v-tooltip="`Open group`"
			>
				<v-icon size="16">fa: fa-light fa-folder-open</v-icon>
			</v-btn>
			<v-btn
				@click="emit('clone-rule')"
				variant="text"
				icon
				size="small"
				density="comfortable"
				color="primary"
				v-tooltip="`Clone`"
			>
				<v-icon size="16">fa: fa-light fa-clone</v-icon>
			</v-btn>
			<v-btn
				@click="emit('delete-rule')"
				variant="text"
				icon
				size="small"
				density="comfortable"
				color="error"
				v-tooltip="`Delete`"
			>
				<v-icon size="16">fa: fa-light fa-trash-can</v-icon>
			</v-btn>
		</template>
	</base-display-rule>
</template>

<style scoped lang="scss">
.contents-summary {
	font-size: 0.8rem;
	opacity: 0.6;
}

.ai-summary-line {
	font-size: 0.8rem;
	opacity: 0.7;
	margin-bottom: 0.5rem;
	overflow-wrap: anywhere;
}
</style>
